import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';

export default function WarehouseGuide() {
  return (
    <>
      <JsonLd
        schema={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: 'Sugargoo Warehouse Guide 2026: Storage, QC Photos & Shipping Prep',
          description: 'Complete guide to the Sugargoo warehouse system including storage time, QC photos, inspection process, and how to prepare your parcel for international shipping.',
          datePublished: '2026-02-15',
          dateModified: '2026-08-06',
          author: { '@type': 'Organization', name: 'Sugargoo Sheet Help' },
          publisher: { '@type': 'Organization', name: 'Sugargoo Sheet Help' },
          mainEntityOfPage: { '@type': 'WebPage', '@id': 'https://sugargoosheet.help/sugargoo-warehouse-guide' },
        }}
      />
      <JsonLd
        schema={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://sugargoosheet.help/' },
            { '@type': 'ListItem', position: 2, name: 'Sugargoo Warehouse Guide 2026', item: 'https://sugargoosheet.help/sugargoo-warehouse-guide' },
          ],
        }}
      />
      <JsonLd
        schema={{
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: [
            { '@type': 'Question', name: 'How long can items stay in the warehouse for free?', acceptedAnswer: { '@type': 'Answer', text: 'Sugargoo offers free warehouse storage for 90 days from the date each item arrives. After this period, extended storage fees may apply.' } },
            { '@type': 'Question', name: 'Can I return items from the warehouse?', acceptedAnswer: { '@type': 'Answer', text: 'Yes, you can request returns to sellers for items that do not meet your expectations. There is typically a small processing fee.' } },
            { '@type': 'Question', name: 'What happens if an item is damaged when it arrives at the warehouse?', acceptedAnswer: { '@type': 'Answer', text: 'Warehouse staff document damage in QC photos and notes. You can request a return or exchange based on the documented condition.' } },
            { '@type': 'Question', name: 'Can I request items be packaged in a specific way?', acceptedAnswer: { '@type': 'Answer', text: 'Yes, during parcel submission you can specify packaging preferences including removing shoe boxes, removing tags, requesting waterproof wrapping, or adding bubble wrap protection.' } },
            { '@type': 'Question', name: 'How do I know when all my items have arrived at the warehouse?', acceptedAnswer: { '@type': 'Answer', text: 'Your Sugargoo dashboard shows real-time status updates for every order. Items marked as Arrived in the warehouse section are ready for QC photos and eventual consolidation.' } },
          ],
        }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main>
          <article className="pt-24 md:pt-28 pb-16 md:pb-20">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <nav className="flex items-center gap-1.5 text-xs text-foreground-500 font-label mb-6 flex-wrap">
                <Link to="/" className="hover:text-primary-600 transition-colors">Home</Link>
                <i className="ri-arrow-right-s-line text-[10px]"></i>
                <span className="text-foreground-700">Sugargoo Warehouse Guide</span>
              </nav>

              <div className="mb-8">
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <span className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-label font-medium">Warehouse</span>
                  <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-label font-medium">2026 Edition</span>
                </div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-900 leading-tight mb-4">
                  Sugargoo Warehouse Guide 2026: Storage, QC Photos &amp; Shipping Prep
                </h1>
                <p className="text-lg text-foreground-600 leading-relaxed max-w-3xl">
                  Understanding the Sugargoo warehouse system is essential for a smooth shopping experience. Learn about storage limits, quality control photos, inspection processes, and how to efficiently prepare your consolidated parcel for international delivery.
                </p>
              </div>

              <div className="aspect-[21/9] w-full rounded-2xl overflow-hidden bg-background-200 mb-10">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20warehouse%20interior%20with%20organized%20shelves%20of%20packaged%20boxes%2C%20warm%20industrial%20lighting%20on%20a%20clean%20concrete%20floor%2C%20minimal%20aesthetic%20with%20cream%20and%20neutral%20tones%2C%20editorial%20logistics%20photography%20with%20soft%20natural%20light&width=1200&height=514&seq=warehouse-hero-2026&orientation=landscape"
                  alt="Sugargoo Warehouse Guide 2026 - organized warehouse storage and QC inspection"
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="flex flex-col lg:flex-row gap-8 md:gap-12">
                <div className="flex-1">
                  <div className="prose-custom">
                    <h2>What Is the Sugargoo Warehouse?</h2>
                    <p>
                      The Sugargoo warehouse is the central logistics hub where all your purchased items are received, inspected, photographed, stored, and consolidated before international shipping. Think of it as your personal receiving and distribution center in China. When you place orders through Sugargoo, sellers ship your items to this warehouse address. Once everything arrives, warehouse staff photograph each item, check for defects, and store your goods safely until you are ready to ship.
                    </p>

                    <h2>Warehouse Storage Time and Limits</h2>
                    <p>
                      Sugargoo provides <strong>free warehouse storage for up to 90 days</strong> from the date an item arrives. This generous window allows you to place multiple orders over several weeks and consolidate them into a single shipment, maximizing shipping efficiency. After the 90-day period, extended storage fees may apply, so it is important to monitor your inventory and ship items within the free window.
                    </p>
                    <p>
                      There is no strict limit on the number of items you can store simultaneously, though extremely large inventories may be subject to management discussions. Most users find the 90-day free storage more than adequate for their shopping cycles, typically consolidating and shipping parcels every 2-4 weeks.
                    </p>

                    <h2>Understanding QC Photos</h2>
                    <p>
                      <strong>Quality Control (QC) photos</strong> are one of Sugargoo's most valuable features. When your items arrive at the warehouse, staff photograph them from multiple angles &mdash; typically front, back, sides, and close-ups of details, logos, and tags. These photos are uploaded to your account within 24-48 hours of item arrival, allowing you to inspect product quality before committing to international shipping.
                    </p>

                    <h3>What to Look for in QC Photos</h3>
                    <p>
                      When reviewing QC photos, examine stitching quality, material texture, color accuracy compared to listing photos, print alignment, logo placement, and any visible defects. For shoes, check sole quality, stitching around the toe box, and tongue construction. For clothing, inspect seams, tags, and fabric quality. If anything appears significantly different from the listing or below expected quality standards, you can request a return directly through the platform.
                    </p>

                    <h3>Requesting Additional QC Photos</h3>
                    <p>
                      If the standard QC photos do not show the detail you need, you can request additional images from specific angles. While this service may incur a small fee, it is invaluable for high-value items where quality verification is critical. Many experienced shoppers request close-up photos of logos, tags, and stitching on luxury or premium items.
                    </p>

                    <h2>The Warehouse Inspection Process</h2>
                    <p>
                      Beyond photography, warehouse staff perform basic inspections to ensure items match your order specifications. They verify that sizes, colors, and quantities are correct. While they do not perform detailed authenticity assessments, they will flag obvious issues like damaged packaging, wrong items, or significant defects. This inspection layer catches many problems before items are shipped internationally, where returns become significantly more complicated.
                    </p>

                    <div className="overflow-x-auto rounded-xl border border-background-200/70 my-6">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-background-100">
                            <th className="text-left px-4 py-3 font-label font-semibold text-foreground-800 text-xs uppercase tracking-wider">Warehouse Feature</th>
                            <th className="text-left px-4 py-3 font-label font-semibold text-foreground-800 text-xs uppercase tracking-wider">Included?</th>
                            <th className="text-left px-4 py-3 font-label font-semibold text-foreground-800 text-xs uppercase tracking-wider">Details</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-t border-background-200/50">
                            <td className="px-4 py-3 text-foreground-800 font-medium">QC Photos</td>
                            <td className="px-4 py-3 text-accent-700 font-medium">Yes &mdash; Free</td>
                            <td className="px-4 py-3 text-foreground-600">Multi-angle photos per item</td>
                          </tr>
                          <tr className="border-t border-background-200/50">
                            <td className="px-4 py-3 text-foreground-800 font-medium">Storage</td>
                            <td className="px-4 py-3 text-accent-700 font-medium">Yes &mdash; 90 days free</td>
                            <td className="px-4 py-3 text-foreground-600">Extended storage has fees</td>
                          </tr>
                          <tr className="border-t border-background-200/50">
                            <td className="px-4 py-3 text-foreground-800 font-medium">Basic Inspection</td>
                            <td className="px-4 py-3 text-accent-700 font-medium">Yes &mdash; Free</td>
                            <td className="px-4 py-3 text-foreground-600">Size, color, quantity check</td>
                          </tr>
                          <tr className="border-t border-background-200/50">
                            <td className="px-4 py-3 text-foreground-800 font-medium">Packaging Removal</td>
                            <td className="px-4 py-3 text-accent-700 font-medium">Yes &mdash; Free</td>
                            <td className="px-4 py-3 text-foreground-600">Reduce weight and volume</td>
                          </tr>
                          <tr className="border-t border-background-200/50">
                            <td className="px-4 py-3 text-foreground-800 font-medium">Returns to Sellers</td>
                            <td className="px-4 py-3 text-primary-700 font-medium">Yes &mdash; Small fee</td>
                            <td className="px-4 py-3 text-foreground-600">Process through platform</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    <h2>How to Prepare Your Parcel for Shipping</h2>

                    <h3>Step 1: Review All QC Photos</h3>
                    <p>
                      Before submitting your parcel for shipping, carefully review all QC photos for every item in your warehouse inventory. Confirm that quality meets your expectations, sizes are correct, and colors match your order. If any items are unsatisfactory, initiate a return request before proceeding.
                    </p>

                    <h3>Step 2: Select Packaging Preferences</h3>
                    <p>
                      Decide whether to keep original packaging (shoe boxes, branded bags, tags) or request removal. For most orders, removing packaging reduces both weight and volume, directly lowering shipping costs. However, if you value presentation or plan to resell, keeping original packaging may be worth the additional shipping expense.
                    </p>

                    <h3>Step 3: Choose Shipping Line and Insurance</h3>
                    <p>
                      Select your preferred shipping line based on speed requirements, budget, and destination. Add shipping insurance for high-value parcels or items that would be difficult to replace. Review the estimated shipping cost and delivery timeframe before confirming your submission.
                    </p>

                    <h3>Step 4: Declare Customs Value</h3>
                    <p>
                      Accurately declare the value of your parcel for customs purposes. Under-declaring may save on potential duties but risks customs seizure or penalties. Over-declaring ensures full insurance coverage but may increase duty obligations. Research your country's import thresholds and regulations to make an informed declaration.
                    </p>

                    <div className="cta-card">
                      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                        <div className="flex-1">
                          <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground-900 mb-2">Build Your Warehouse Inventory</h3>
                          <p className="text-sm text-foreground-600">Find quality products to add to your warehouse using the organized Sugargoo Spreadsheet database.</p>
                        </div>
                        <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-white text-sm font-label font-semibold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap flex-shrink-0"
                        >
                          Sugargoo Spreadsheet
                          <i className="ri-arrow-right-up-line"></i>
                        </a>
                      </div>
                    </div>

                    <h2>Warehouse FAQ</h2>

                    <div className="space-y-4 my-6">
                      {[
                        {
                          q: 'How long can items stay in the warehouse for free?',
                          a: 'Sugargoo offers free warehouse storage for 90 days from the date each item arrives. After this period, extended storage fees may apply.',
                        },
                        {
                          q: 'Can I return items from the warehouse?',
                          a: 'Yes, you can request returns to sellers for items that do not meet your expectations. There is typically a small processing fee, and the return must be initiated within a reasonable timeframe after the item arrives.',
                        },
                        {
                          q: 'What happens if an item is damaged when it arrives at the warehouse?',
                          a: 'Warehouse staff document damage in QC photos and notes. You can request a return or exchange based on the documented condition. Contact customer support immediately if damage was not caused by shipping.',
                        },
                        {
                          q: 'Can I request items be packaged in a specific way?',
                          a: 'Yes, during parcel submission you can specify packaging preferences including removing or keeping shoe boxes, removing tags, requesting waterproof wrapping, or adding bubble wrap protection.',
                        },
                        {
                          q: 'How do I know when all my items have arrived at the warehouse?',
                          a: 'Your Sugargoo dashboard shows real-time status updates for every order. Items marked as "Arrived" in the warehouse section are ready for QC photos and eventual consolidation.',
                        },
                      ].map((faq, i) => (
                        <div key={i} className="bg-background-100 border border-background-200/70 rounded-xl p-5">
                          <h4 className="font-heading text-base font-semibold text-foreground-900 mb-2">{faq.q}</h4>
                          <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mt-10 p-6 md:p-8 bg-background-100 border border-background-200/70 rounded-2xl">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                      <div className="flex-1">
                        <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground-900 mb-2">Start Your Warehouse Journey</h3>
                        <p className="text-sm text-foreground-600">Browse verified finds and build your first warehouse collection today.</p>
                      </div>
                      <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-white text-sm font-label font-semibold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap flex-shrink-0"
                      >
                        Buy It Now
                        <i className="ri-arrow-right-up-line"></i>
                      </a>
                    </div>
                  </div>

                  <div className="mt-8 pt-6 border-t border-background-200/70 flex flex-wrap gap-4">
                    <Link to="/" className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-label font-medium transition-colors"
                    >
                      <i className="ri-arrow-left-line"></i>Home
                    </Link>
                    <Link to="/sugargoo-spreadsheet-guide" className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-label font-medium transition-colors"
                    >
                      Spreadsheet Guide
                      <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                </div>

                <aside className="lg:w-72 flex-shrink-0">
                  <div className="sticky top-24 space-y-6">
                    <div className="bg-background-100 border border-background-200/70 rounded-xl p-5">
                      <h4 className="font-label text-sm font-semibold text-foreground-900 mb-3 uppercase tracking-wider">Quick Links</h4>
                      <div className="space-y-2.5">
                        <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-label font-medium transition-colors"
                        >
                          <i className="ri-external-link-line"></i>Sugargoo Spreadsheet
                        </a>
                        <Link to="/" className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-label font-medium transition-colors"
                        >
                          <i className="ri-home-line"></i>Homepage
                        </Link>
                      </div>
                    </div>
                    <div className="bg-accent-100 border border-accent-200/50 rounded-xl p-5">
                      <h4 className="font-label text-sm font-semibold text-accent-800 mb-3 uppercase tracking-wider">Related Guides</h4>
                      <div className="space-y-2.5">
                        <Link to="/sugargoo-spreadsheet-guide" className="block text-sm text-accent-700 hover:text-accent-800 transition-colors">Spreadsheet Guide</Link>
                        <Link to="/sugargoo-shipping-cost-guide" className="block text-sm text-accent-700 hover:text-accent-800 transition-colors">Shipping Cost Guide</Link>
                        <Link to="/sugargoo-review-2026" className="block text-sm text-accent-700 hover:text-accent-800 transition-colors">Sugargoo Review</Link>
                        <Link to="/sugargoo-coupon-discount-code" className="block text-sm text-accent-700 hover:text-accent-800 transition-colors">Coupon Guide</Link>
                      </div>
                    </div>
                  </div>
                </aside>
              </div>
            </div>
          </article>
        </main>
        <Footer />
      </div>
    </>
  );
}